import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer, FilterTabs, StatusBadge } from '@/features/admin/ui';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { adminQueries, auditLog, type WaitlistEntry } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/invites')({
  component: InvitesPage,
});

const TABS = [
  { value: 'codes', label: 'Codes' },
  { value: 'waitlist', label: 'Waitlist' },
] as const;

function InvitesPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 on direct nav (Spec 21)
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('codes');
  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Access" title="Invites" />
      <div className="mt-6">
        <FilterTabs options={TABS} value={tab} onChange={setTab} />
      </div>
      {tab === 'codes' ? <CodesTab /> : <WaitlistTab />}
    </div>
  );
}

const CODE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unused', label: 'Unused' },
  { value: 'used', label: 'Used' },
  { value: 'expired', label: 'Expired' },
] as const;

function CodesTab() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof CODE_FILTERS)[number]['value']>('all');
  const [genOpen, setGenOpen] = useState(false);
  const list = useQuery(adminQueries.invites(filter));
  const rows = list.data ?? [];

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between gap-4">
        <FilterTabs options={CODE_FILTERS} value={filter} onChange={setFilter} />
        <button
          type="button"
          onClick={() => setGenOpen(true)}
          className="shrink-0 rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Generate
        </button>
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">No codes.</p>
      ) : (
        <ul className="mt-6 divide-y divide-ink/10">
          {rows.map((c) => {
            const expired =
              !c.used_at && c.expires_at && new Date(c.expires_at).getTime() <= Date.now();
            const status = c.used_at ? 'used' : expired ? 'expired' : 'unused';
            return (
              <li key={c.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(c.code);
                      toast.success('Code copied.');
                    }}
                    className="font-mono text-sm text-ink hover:text-oxblood"
                    title="Copy"
                  >
                    {c.code}
                  </button>
                  <p className="font-sans text-xs text-ink/50">
                    {c.note ?? 'No note'} ·{' '}
                    {c.used_at
                      ? `used by ${c.used?.display_name ?? c.used?.username ?? 'someone'}`
                      : `created ${new Date(c.created_at).toLocaleDateString()}`}
                  </p>
                </div>
                <StatusBadge
                  status={
                    status === 'unused' ? 'pending' : status === 'used' ? 'converted' : 'rejected'
                  }
                />
              </li>
            );
          })}
        </ul>
      )}

      <Drawer open={genOpen} onClose={() => setGenOpen(false)} title="Generate invite codes">
        <GenerateForm
          onDone={() => {
            setGenOpen(false);
            void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
          }}
        />
      </Drawer>
    </div>
  );
}

function GenerateForm({ onDone }: { onDone: () => void }) {
  const [note, setNote] = useState('');
  const [count, setCount] = useState(1);
  const [expiresDays, setExpiresDays] = useState('');

  const gen = useMutation({
    mutationFn: async () => {
      const n = Math.max(1, Math.min(100, Math.floor(count) || 1));
      const { data, error } = await supabase.functions.invoke('admin-create-invite', {
        body: {
          note: note.trim() || undefined,
          count: n,
          expires_in_days: expiresDays ? Number(expiresDays) : undefined,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Codes generated.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Generation failed.'),
  });

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Note (optional)
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Founder batch — launch announcement"
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </div>
      <div className="flex gap-4">
        <div>
          <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
            How many (1–100)
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-28 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
            Expires in days (optional)
          </label>
          <input
            type="number"
            min={1}
            value={expiresDays}
            onChange={(e) => setExpiresDays(e.target.value)}
            className="w-36 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
          />
        </div>
      </div>
      <button
        type="button"
        disabled={gen.isPending}
        onClick={() => gen.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Generate {count > 1 ? `${count} codes` : 'code'}
      </button>
    </div>
  );
}

const WL_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'invited', label: 'Invited' },
  { value: 'converted', label: 'Converted' },
  { value: 'declined', label: 'Declined' },
] as const;

function WaitlistTab() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof WL_FILTERS)[number]['value']>('');
  const [inviting, setInviting] = useState<WaitlistEntry | null>(null);
  const list = useQuery(adminQueries.waitlist(filter));
  const rows = list.data ?? [];

  const decline = useMutation({
    mutationFn: async (row: WaitlistEntry) => {
      const { error } = await supabase
        .from('waitlist')
        .update({ status: 'declined' })
        .eq('id', row.id);
      if (error) throw error;
      await auditLog('waitlist.declined', 'waitlist', row.id, {});
    },
    onSuccess: () => {
      toast.success('Marked declined.');
      void qc.invalidateQueries({ queryKey: ['admin', 'waitlist'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  return (
    <div className="mt-6">
      <FilterTabs options={WL_FILTERS} value={filter} onChange={setFilter} />
      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">No waitlist entries.</p>
      ) : (
        <ul className="mt-6 divide-y divide-ink/10">
          {rows.map((w) => (
            <li key={w.id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="font-serif text-base text-ink">{w.display_name}</p>
                <p className="font-sans text-xs text-ink/50">
                  {w.email} · {new Date(w.created_at).toLocaleDateString()}
                </p>
                {w.intro && (
                  <p className="mt-1 line-clamp-1 font-sans text-sm text-ink/60">{w.intro}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge status={w.status} />
                {/* Invite is offered for declined entries too — minds change. */}
                {(w.status === 'pending' || w.status === 'declined') && (
                  <button
                    type="button"
                    onClick={() => setInviting(w)}
                    className="rounded-md bg-ink px-3 py-1.5 font-sans text-xs font-medium text-parchment hover:bg-oxblood"
                  >
                    Invite
                  </button>
                )}
                {w.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => decline.mutate(w)}
                    className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs text-ink hover:border-oxblood"
                  >
                    Decline
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(inviting)}
        onClose={() => setInviting(null)}
        title={`Invite ${inviting?.display_name ?? ''}`}
      >
        {inviting && (
          <InviteForm
            entry={inviting}
            onDone={() => {
              setInviting(null);
              void qc.invalidateQueries({ queryKey: ['admin', 'waitlist'] });
              void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function InviteForm({ entry, onDone }: { entry: WaitlistEntry; onDone: () => void }) {
  const [message, setMessage] = useState('');

  const send = useMutation({
    mutationFn: async () => {
      // 1) generate a fresh single-use code
      const { data: created, error: genErr } = await supabase.functions.invoke(
        'admin-create-invite',
        { body: { note: `Waitlist invite — ${entry.email}` } },
      );
      if (genErr) throw genErr;
      const codeId = (created as { id?: string })?.id;
      if (!codeId) throw new Error('Code generation returned no id.');

      // 2) send it and flip the waitlist row to invited
      const { data: sent, error: sendErr } = await supabase.functions.invoke('admin-send-invite', {
        body: {
          waitlist_id: entry.id,
          code_id: codeId,
          custom_message: message.trim() || undefined,
        },
      });
      if (sendErr) throw sendErr;
      return sent as { email?: string };
    },
    onSuccess: (r) => {
      toast.success(
        r?.email === 'sent'
          ? 'Invite sent.'
          : r?.email === 'skipped'
            ? 'Invited (email skipped — no Resend key).'
            : 'Invited (email failed — resend manually).',
      );
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  return (
    <div className="space-y-4">
      <p className="font-sans text-sm text-ink/70">
        Generates a single-use code and emails it to <span className="text-ink">{entry.email}</span>
        .
      </p>
      <div>
        <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Personal note (optional)
        </label>
        <textarea
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </div>
      <button
        type="button"
        disabled={send.isPending}
        onClick={() => send.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Generate code + send invite
      </button>
    </div>
  );
}
